import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {DataTracingService} from "@features/data-tracing/services/data-tracing.service";
import {Subject, BehaviorSubject} from 'rxjs';
import {filter, takeUntil} from "rxjs/operators";
import {FlexSheetService} from "@features/spreadsheet/services/flex-sheet.service";
import {PlatformQueryService} from "@core/services/platform-query.service";
import {select, Store} from "@ngrx/store";
import {getCurrentPlatformLink, ParticleState} from "@shared/store/reducers/particle.reduer";
import {MagicLinkModel} from "@shared/models/platform-tool-models";
import {NavigationEnd, Router} from "@angular/router";
import {ActivityRightSidebarStateService} from "@features/data-tracing/services/activity-right-sidebar-state.service";
import {ActivityComponents} from "@features/data-tracing/constants/activity-components";
import {SocketClusterService} from "@core/services/socket-cluster.service";
import {ActivityFeedInfo, ActivityFeedType, DefaultActivityFilterSettings} from '@features/task-manager/collaboration/store/activity.model';
import {ActivityFilterSettings} from '@shared/models/activity-tracker-header.model';
import {ActivityFilterService} from '@features/task-manager/collaboration/services/activity-filter.service';
import {DataContextFeedService} from '@shared/services/data-context-feed/data-context-feed.service';

@Component({
    standalone: false,
    selector: 'app-data-sources-root',
  templateUrl: './data-sources-root.component.html',
  styleUrls: ['./data-sources-root.component.scss']
})
export class DataSourcesRootComponent implements OnInit, OnDestroy{

  activityFeedTypes = ActivityFeedType;
  public data: any;

  activityFeeds: ActivityFeedInfo[] = [];

  title: string;
  dataEditSummaryTitle : string;
  dataEditSummaryId: string;
  dataTracingChannel: string;
  showChatEditor = false;

  platformLinkModel: MagicLinkModel;
  isReceivedPlatformLinkFirstTime: boolean = true;

  selectedTabIndex: number = 0;

  activityFeedLoaded: boolean;
  activityFeedOpened: boolean;
  dataSourcesOpened: boolean;
  dataSourcesLoaded: boolean;
  dataSourcesFieldHistoryOpened: boolean;
  dataSourcesFieldHistoryLoaded: boolean;

  invalidateDataSources: boolean;
  invalidateDataSourcesFieldHistory: boolean;

  unSubscribe$: Subject<void> = new Subject<void>();
  dataTracingChannelUnSubscriber$ = new Subject<void>();

  activitiesRootEvent$: BehaviorSubject<any> = new BehaviorSubject(null);
  // HS-E-21-12: the menu's "Support activities only"; the activity cards filter their feeds on it.
  supportOnly = false;

  filterSettings: ActivityFilterSettings = JSON.parse(JSON.stringify(DefaultActivityFilterSettings));

  //@ViewChild('dynamicComponent', {read: ViewContainerRef}) viewContainerRef;

  constructor(
    private dataTracingService: DataTracingService,
    private platformQueryService: PlatformQueryService,
    private flexSheetService: FlexSheetService,
    private store: Store<ParticleState>,
    private router: Router,
    private activityRightSidebarStateService: ActivityRightSidebarStateService,
    private socketClusterService: SocketClusterService,
    private changeDetectorRef: ChangeDetectorRef,
    private activityFilterService: ActivityFilterService,
    private dataContextFeedService: DataContextFeedService) {}

  ngOnInit(): void {
    console.log('DataSourcesRootComponent onInIt Dispatched Data: ', this.data);

    this.router.events.pipe(takeUntil(this.unSubscribe$), filter((e) => e instanceof NavigationEnd)).subscribe((e) => {
      console.log('DataSourcesRootComponent route change: ', e);
      this.clearDataTracingData();
    });

    this.store.pipe(takeUntil(this.unSubscribe$), select(getCurrentPlatformLink)).subscribe(state => {
      console.log('DataSourceRootComponent PlatformLinkModel Changed', state);
      this.takeActionOnMagicLinkModelChange(state);
    });

    this.dataTracingService.getDataSourcesActionObservable().pipe(takeUntil(this.unSubscribe$)).subscribe(action => {
      this.handleAction(action);
    });

    this.takeActionOnInitialDispatchedData();
  }

  ngOnDestroy(): void {
    console.log('DataSourcesRootComponent onDestroy');
    this.activityRightSidebarStateService.clearAllStates();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.unSubscribeToCurrentDataTracingChannel();
    this.dataTracingChannelUnSubscriber$.complete();
  }

  takeActionOnInitialDispatchedData() {
    console.log('DataSourcesRootComponent handleInitialDispatchedData');
    if (this.data?.actionType) { // can handle based on actionType
      this.handleAction({
        actionType: this.data.actionType,
        data: this.data
      });
    } else { // default action open the activity feed
      this.triggerShowActivityFeedInfoAction();
    }
  }

  takeActionOnMagicLinkModelChange(newPlatformLinkModel: MagicLinkModel) {
    this.platformLinkModel = newPlatformLinkModel;

    if (this.isReceivedPlatformLinkFirstTime) {
      this.isReceivedPlatformLinkFirstTime = false;
      return;
    }
    console.log('DataSourceRootComponent takeActionOnMagicLinkModelChange not first time changed');

    this.clearDataTracingData();

    this.triggerShowActivityFeedInfoAction();
  }

  private unSubscribeToCurrentDataTracingChannel() {
    if (this.dataTracingChannel) {
      this.dataTracingChannelUnSubscriber$.next();
      this.dataTracingChannel = null;
    }
  }

  private subscribeToCurrentDataTracingChannel() {
    if (this.dataTracingChannel === this.dataEditSummaryId) {
      console.log('Already Subscribed to DataTracingChannel', this.dataTracingChannel);
      return;
    }

    if (this.dataTracingChannel) {
      console.log('There is already a subscriber in channel.', this.dataTracingChannel);
      this.unSubscribeToCurrentDataTracingChannel();
      //this.socketClusterService.unsubscribeChannel(this.getDataTracingChannelName(this.dataTracingChannel));
      console.log('Unsubscribed to DataTracingChannel', this.dataTracingChannel);
    }

    this.dataTracingChannel = this.dataEditSummaryId;

    if (!this.dataTracingChannel || this.dataTracingChannel === '') {
      console.log('DataTracingChannel or dataEditSummary empty. So skipping the channel subscription');
      return;
    }

    console.log('Subscribing to DataTracingChannel', this.dataTracingChannel);

    const channelName = this.getDataTracingChannelName(this.dataTracingChannel);

    this.socketClusterService.subscribeToChannel(channelName).pipe(takeUntil(this.dataTracingChannelUnSubscriber$)).subscribe(notification => {
      console.log('DataSourcesRootComponent notification', notification);

      if (notification.EventType === "InvalidateDataTracing" && notification.EventId === this.dataEditSummaryId) {
        this.invalidateDataTracing();
      }
    });
  }

  private getDataTracingChannelName(dataEditSummaryId: string) {
    const channelName = 'DataTracing-' + dataEditSummaryId;
    return channelName;
  }

  private invalidateDataTracing() {
    if (this.dataSourcesFieldHistoryLoaded) {
      this.invalidateDataSourcesFieldHistory = true;
    }
    if (this.dataSourcesLoaded) {
      this.invalidateDataSources = true;
    }
  }

  clearDataTracingData() {
    this.unSubscribeToCurrentDataTracingChannel();
    this.dataEditSummaryTitle = null;
    this.dataEditSummaryId = null;
    this.dataSourcesOpened = false;
    this.dataSourcesLoaded = false;
    this.dataSourcesFieldHistoryOpened = false;
    this.dataSourcesFieldHistoryLoaded = false;

    this.activityRightSidebarStateService.unloadActivityRightSidebarComponent(ActivityComponents.DataSourceComponent);
    this.activityRightSidebarStateService.unloadActivityRightSidebarComponent(ActivityComponents.DataTracingHistoryDetailsComponent);

    if (this.data?.DataEditSummaryId) {
      this.data.DataEditSummaryId = null;
    }
    if (this.data?.SourceType) {
      this.data.SourceType = null;
    }
  }

  handleAction(action: any): void {
    if (action?.data?.title) {
      this.title = action.data.title;
    }
    console.log('DataSourcesRootComponent handleAction', action);
    switch (action.actionType) {
      case 'ShowDataSources':
        this.activityFeedOpened = false;
        this.dataSourcesFieldHistoryOpened = false;
        this.showDataSources(action);
        break;
      case 'ShowDataSourcesFieldHistory':
        this.activityFeedOpened = false;
        // this.dataSourcesOpened = false; //need to keep track  where we came from to navigate back from ShowDataSourcesFieldHistory
        this.showDataSourcesFieldHistory(action);
        break;
      case 'ShowActivityFeedInfo':
        this.dataSourcesFieldHistoryOpened = false;
        this.dataSourcesOpened = false;
        this.showActivityFeedInfo(action);
        break;
      case 'ShowActivityFeedsInfo':
        this.dataSourcesFieldHistoryOpened = false;
        this.dataSourcesOpened = false;
        this.showActivityFeedsInfo(action);
        break;
      default:
        console.error('No action handler found for the given action', action);
    }
  }

  handleDataSourcesEvent(event: any): void {
    console.log('handleDataSourcesEvent', event);
    switch (event?.type) {
      case 'RecordClicked':
        this.handleAction({
          actionType: 'ShowDataSourcesFieldHistory',
          data: event?.data
        });
        break;
      case 'DataTracingResponseReceived':
        this.adjustDataEditSummary(event.data);
        break;
    }
  }

  handleActivityFeedInfoEvent(event: any): void {
    console.log('activityFeedInfoChangedEvent', event);
    switch (event?.type) {
      case 'ActivityFeedInfoChanged':
        if (event?.data?.title) {
          this.title = event?.data?.title;
          this.changeDetectorRef.detectChanges();
        }
        break;
      case 'ChatEditorStateChanged':
        this.showChatEditor = event?.chatEditorOpened;
        break;
    }
  }

  handleFieldHistoryEvent(event: any): void {
    console.log('handleFieldHistoryEvent', event);
    switch (event?.type) {
      case 'BackClicked':
        this.dataSourcesFieldHistoryLoaded = false;
        this.dataSourcesFieldHistoryOpened = false;
        this.activityRightSidebarStateService.unloadActivityRightSidebarComponent(ActivityComponents.DataTracingHistoryDetailsComponent);
        if (this.dataSourcesOpened) {
          this.triggerShowDataSourcesAction();
        } else {
          this.triggerShowActivityFeedInfoAction();
        }
        break;
      case 'DataEditFieldHistoryReceived':
        this.adjustDataEditSummary(event.data);
        break;
      case 'AddCommentClicked':
        this.data = event.data;
        this.showChatEditor = true;
        this.activityFeedLoaded = false;
        this.triggerShowActivityFeedInfoAction();
        break;
    }
  }

  adjustDataEditSummary(data) {
    console.log('AdjustDataEditSummary', data);
    if (!data) return;
    if (data.DataEditSummaryTitle) {
      this.dataEditSummaryTitle = data.DataEditSummaryTitle;
      this.title = this.dataEditSummaryTitle;
      this.changeDetectorRef.detectChanges();
    }
    if (data.DataEditSummaryId) {
      if (this.dataEditSummaryId !== data.DataEditSummaryId) {
        this.dataEditSummaryId = data.DataEditSummaryId;
        this.subscribeToCurrentDataTracingChannel();
      }
    }
  }

  showDataSources(action: any) {
    console.log('DataSourcesRootComponent showDataSources action: ', action);
    if (action.invalidate && !this.dataSourcesOpened) {
      console.log('DataSources Component not opened. Can not invalidate');
      return;
    }
    this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.DataSourceComponent);
    if (action.invalidate) {
      this.invalidateDataSources = true;
      return;
    }
    if (this.dataEditSummaryTitle) {
      this.title = this.dataEditSummaryTitle;
    }
    this.invalidateDataSources = false;
    this.data = action.data;
    this.prepareData();
    this.selectedTabIndex = 1;
    this.dataSourcesOpened = true;
    this.dataSourcesLoaded = true;
  }

  showDataSourcesFieldHistory(action: any) {
    console.log('DataSourcesRootComponent showDataSourcesFieldHistory action: ', action, this.selectedTabIndex);
    if (action.invalidate && !this.dataSourcesFieldHistoryOpened) {
      console.log('DataSourcesFieldHistory Component not opened. Can not invalidate');
      return;
    }
    this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.DataTracingHistoryDetailsComponent);
    if (action.invalidate) {
      this.invalidateDataSourcesFieldHistory = true;
      return;
    }
    console.log('showDataSourcesFieldHistory dataSourcesFieldHistoryLoaded', this.dataSourcesFieldHistoryLoaded);
    if (this.dataEditSummaryTitle) {
      this.title = this.dataEditSummaryTitle;
    }
    this.invalidateDataSourcesFieldHistory = false;
    this.data = action.data;
    this.prepareData();
    this.selectedTabIndex = 0;
    this.activityFeedLoaded = false;
    this.dataSourcesFieldHistoryOpened = true;
    this.dataSourcesFieldHistoryLoaded = true;
  }

  showActivityFeedInfo(action: any) {
    console.log('DataSourcesRootComponent showActivityFeedInfo action: ', action);
    this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.ActivityFeedComponent);
    if (this.dataContextFeedService.getShowActivityChatEditor()) {
      this.showChatEditor = true;
      this.dataContextFeedService.setShowActivityChatEditor(false);
    }
    this.data = action.data;
    this.activityFeeds = [];
    this.selectedTabIndex = 0;
    this.activityFeedOpened = true;
    this.activityFeedLoaded = true;
  }
  showActivityFeedsInfo(action: any) {
    console.log('DataSourcesRootComponent showActivityFeedInfo action: ', action);
    this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.ActivityFeedComponent);
    this.activityFeeds = action.data;
    if (this.dataContextFeedService.getShowActivityChatEditor()) {
      this.showChatEditor = true;
      this.dataContextFeedService.setShowActivityChatEditor(false);
    }
    this.data = this.platformLinkModel?.activityFeedInfo;
    this.selectedTabIndex = 0;
    this.activityFeedOpened = true;
    this.activityFeedLoaded = true;
  }

  triggerShowActivityFeedInfoAction() {
    if (this.platformLinkModel?.activityFeedsInfo?.length > 0) {
      this.handleAction({
        actionType: 'ShowActivityFeedsInfo',
        data: this.platformLinkModel.activityFeedsInfo
      });
      return;
    }
    this.handleAction({
      actionType: 'ShowActivityFeedInfo',
      data: this.platformLinkModel?.activityFeedInfo
    });
  }

  triggerShowDataSourcesAction() {
      this.handleAction({
        actionType: 'ShowDataSources',
        data: this.data
      });
  }

  private prepareData(): void {
    if (!this.data) {
      return;
    }
    if (this.data.DataEditSummaryId === '[WORKBOOK_ID]' && this.flexSheetService.getCurrentWorkBook()?.id) {
      this.data.DataEditSummaryId = this.flexSheetService.getCurrentWorkBook()?.id;
    }
  }

  onSelectedTabChange($event) {
    console.log($event);
    if ($event.index === 1) {
      this.activityFeedLoaded = false;
      this.dataSourcesLoaded = false;
      this.changeDetectorRef.detectChanges();
      this.handleAction({
        actionType: 'ShowDataSources',
        data : this.data
      });
    } else {
      console.log('onSelectedTabChange dataSourcesFieldHistoryLoaded', this.dataSourcesFieldHistoryLoaded);
      if (this.dataSourcesFieldHistoryLoaded) {
        this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.DataTracingHistoryDetailsComponent);
        this.dataSourcesFieldHistoryOpened = true;
        this.activityFeedOpened = false;
        // this.dataSourcesOpened = false; //need to keep track  where we came from to navigate back from ShowDataSourcesFieldHistory
      } else {
        if (this.activityFeedLoaded) {
          this.activityRightSidebarStateService.setCurrentActivityRightSidebarComponent(ActivityComponents.ActivityFeedComponent);
          this.activityFeedOpened = true;
          this.dataSourcesOpened = false;
          this.dataSourcesFieldHistoryOpened = false;
        } else {
          this.triggerShowActivityFeedInfoAction();
        }
      }
    }
  }
  refreshFeedsInfo() {
    this.activitiesRootEvent$.next({
      type: 'Refresh'
    });
  }
  activityHeaderDateChange() {
    this.activitiesRootEvent$.next({
      type: 'DateRange'
    });
  }
  onToggleSupportOnly() {
    this.supportOnly = !this.supportOnly;
    this.activitiesRootEvent$.next({
      type: 'SupportOnly',
      value: this.supportOnly
    });
  }
  onExportActivityFeeds() {
    this.activitiesRootEvent$.next({
      type: 'Export'
    });
  }
  onNotificationSettings() {
    this.activitiesRootEvent$.next({
      type: 'Notifications'
    });
  }
}
